import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
} from '@angular/core';

import { ProfileCompletionWidgetDialogService } from '@main-client/src/app/profile-completion-widget-dialog/profile-completion-widget-dialog.service';

import { Account } from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { IAppState } from '@main-client/src/app/app.state';
import { Icebreaker } from '@main-client/src/app/icebreakers/icebreaker.model';
import {
  aboutWidgetConfig,
  experienceEducationWidgetConfig,
  goalsWidgetConfig,
  headlineWidgetConfig,
  icebreakersWidgetConfig,
  interestsWidgetConfig,
  locationWidgetConfig,
  photoWidgetConfig,
  pronounsWidgetConfig,
  segmentsWidgetConfig,
  websiteWidgetConfig,
  WidgetConfig,
} from '@main-client/src/app/profile-completion-widget-view/profile-completion-widget-configs';
import {
  computeProfileStrengthCount,
  computeProfileStrengthText,
  computeStepsToCompletion,
  computeWidgetsIndicesToShow,
} from '@main-client/src/app/profile-completion-widget-view/profile-completion-widget.helper';

import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import includes from 'lodash-es/includes';
import indexOf from 'lodash-es/indexOf';

@Component({
  selector: 'app-profile-completion-widget-view',
  templateUrl: './profile-completion-widget-view.component.html',
})
export class ProfileCompletionWidgetViewComponent implements OnInit, OnChanges {
  @Input() icebreakers: Icebreaker[];
  @Input() group: Group;
  @Input() tenant: Tenant;
  @Input() user: Account;
  validIndices: number[] = [];
  widgetConfigs: WidgetConfig[] = [];
  widgetIndex = 0;
  profileStrengthCount = 1;
  profileStrengthText: string;
  stepsToCompletion: number;

  constructor(
    public widgetDialogService: ProfileCompletionWidgetDialogService,
  ) {}

  ngOnInit() {
    this.validIndices = computeWidgetsIndicesToShow(
      this.user,
      this.group,
      this.tenant?.enabled_features?.profile,
      this.icebreakers,
    );
    this.widgetIndex = this.validIndices[0];
    this.widgetConfigs = [
      {
        ...experienceEducationWidgetConfig,
        addProfileInformation: () => this.openFormDialog(0),
        skip: () => this.next(this.validIndices),
      },
      {
        ...headlineWidgetConfig,
        addProfileInformation: () => this.openFormDialog(1),
        skip: () => this.next(this.validIndices),
      },
      {
        ...photoWidgetConfig,
        addProfileInformation: () => this.openFormDialog(2),
        skip: () => this.next(this.validIndices),
      },
      {
        ...segmentsWidgetConfig,
        group: this.group,
        addProfileInformation: () => this.openFormDialog(3),
        skip: () => this.next(this.validIndices),
      },
      {
        ...locationWidgetConfig,
        addProfileInformation: () => this.openFormDialog(4),
        skip: () => this.next(this.validIndices),
      },
      {
        ...goalsWidgetConfig,
        group: this.group,
        addProfileInformation: () => this.openFormDialog(5),
        skip: () => this.next(this.validIndices),
      },
      {
        ...interestsWidgetConfig,
        group: this.group,
        addProfileInformation: () => this.openFormDialog(6),
        skip: () => this.next(this.validIndices),
      },
      {
        ...icebreakersWidgetConfig,
        addProfileInformation: () => this.openFormDialog(7),
        skip: () => this.next(this.validIndices),
      },
      {
        ...websiteWidgetConfig,
        addProfileInformation: () => this.openFormDialog(8),
        skip: () => this.next(this.validIndices),
      },
      {
        ...aboutWidgetConfig,
        addProfileInformation: () => this.openFormDialog(9),
        skip: () => this.next(this.validIndices),
      },
      {
        ...pronounsWidgetConfig,
        addProfileInformation: () => this.openFormDialog(10),
        skip: () => this.next(this.validIndices),
      },
    ];
    if (!this.tenant.enabled_features?.profile) {
      this.widgetConfigs.pop();
    }
    this.stepsToCompletion = computeStepsToCompletion(
      this.widgetConfigs.length,
      this.group,
      this.icebreakers,
    );
    this.profileStrengthCount = computeProfileStrengthCount(
      this.stepsToCompletion,
      this.validIndices,
    );
    this.profileStrengthText = computeProfileStrengthText(
      this.profileStrengthCount,
    );
  }

  ngOnChanges(changes: { group?: SimpleChange; user?: SimpleChange }) {
    const previousValidIndices = cloneDeep(this.validIndices);
    this.validIndices = computeWidgetsIndicesToShow(
      this.user,
      this.group,
      this.tenant.enabled_features.profile,
      this.icebreakers,
    );
    this.stepsToCompletion = computeStepsToCompletion(
      this.widgetConfigs.length,
      this.group,
      this.icebreakers,
    );
    this.profileStrengthCount = computeProfileStrengthCount(
      this.stepsToCompletion,
      this.validIndices,
    );
    this.profileStrengthText = computeProfileStrengthText(
      this.profileStrengthCount,
    );
    if (
      (!get(changes, 'user.firstChange') &&
        this.widgetDialogService.isDialogOpen) ||
      !includes(this.validIndices, this.widgetIndex)
    ) {
      this.next(previousValidIndices);
    }
  }

  next(validIndices: number[]) {
    const currentIndex = indexOf(validIndices, this.widgetIndex);
    if (validIndices.length - 1 <= currentIndex) {
      this.widgetIndex = validIndices[0];
      return;
    }
    this.widgetIndex = validIndices[currentIndex + 1];
  }

  openFormDialog(formIndex: number) {
    this.widgetDialogService
      .openFormDialog(formIndex, this.group, this.icebreakers, this.tenant)
      .subscribe();
  }
}
